import React, { useEffect, useRef } from "react"

// styles
import Styles from "./KYCVerificationTab.module.css"

// icons
import { FiCheck, FiX } from "react-icons/fi"

// redux
import { useSelector } from "react-redux"

// components
import KYCStatus from "./KYCStatus"

// utils
import { formatDateTime } from "../../../../utility/formatDateTime"

function KYCVerificationTab() {
  const userData = useSelector((state) => state.userData.value)
  const theme = useSelector((state) => state.theme.value)
  const exchangeName = useSelector((state) => state.exchangeCustomization.value.name)

  // Use a ref to store the previous KYC status
  const previousKycStatusRef = useRef(null)

  document.title = `${exchangeName}: KYC`

  const kyc_status = userData?.labels?.find(
    (label) => label.key === "document"
  )?.value

  const kyc_description = userData?.labels?.find(
    (label) => label.key === "description"
  )?.value

  useEffect(() => {
    // Check if status changed from "not_started" to something else
    if (previousKycStatusRef.current === "not_started" &&
      kyc_status !== "not_started" &&
      kyc_status !== null &&
      kyc_status !== undefined) {
      window.location.reload()
    }

    // Update the previous status ref
    previousKycStatusRef.current = kyc_status
  }, [kyc_status])

  const verificationSteps = [
    { id: 'personal', label: 'Personal information', completed: kyc_status === 'verified' },
    { id: 'government', label: 'Government-issued ID', completed: kyc_status === 'verified' },
    { id: 'facial', label: 'Facial recognition', completed: kyc_status === 'verified' }
  ]

  const features = [
    {
      title: 'Fiat & Crypto Deposit',
      description: 'Deposit your hard-earned cash or your crypto stash - either way, we\'ll take it.',
      noKyc: false,
      kycVerified: true
    },
    {
      title: 'Fiat & Crypto Withdrawal',
      description: 'Withdraw your funds whenever you want, wherever you want. We won\'t hold you back.',
      noKyc: false,
      kycVerified: true
    },
    {
      title: 'Spot Trading',
      description: 'Get ready to ride the market wave! Buy, sell, and trade your favourite cryptos in real time without delays or hassle.',
      noKyc: false,
      kycVerified: true
    },
    {
      title: 'Conversion',
      description: 'Not sure which crypto to choose? No worries! Our conversion tool makes it easy to swap one currency for another.',
      noKyc: false,
      kycVerified: true
    }
  ]

  const getStatusDisplay = () => {
    switch (kyc_status) {
      case 'verified':
        return {
          title: 'VERIFIED',
          color: 'var(--success-color)',
          showSteps: true
        }
      case 'pending':
      case 'sdk_pending':
        return {
          title: 'VERIFICATION PENDING',
          color: 'var(--warning-color)',
          showSteps: false
        }
      case 'rejected':
        return {
          title: 'REJECTED',
          color: 'var(--danger-color)',
          showSteps: false
        }
      case 'sdk_started':
        return {
          title: 'KYC IN PROGRESS',
          color: 'var(--warning-color)',
          showSteps: false
        }
      case 'sdk_completed':
        if (kyc_description === 'sdk_completed_rejected') {
          return {
            title: 'REJECTED',
            color: 'var(--danger-color)',
            showSteps: false
          }
        }
        return {
          title: 'AWAITING ADMIN APPROVAL',
          color: 'var(--warning-color)',
          showSteps: false
        }
      case 'sdk_not_started':
      default:
        return {
          title: 'NOT STARTED',
          color: 'var(--gray-color)',
          showSteps: false
        }
    }
  }

  const statusDisplay = getStatusDisplay()

  return (
    <div className={Styles.container}>
      <div className={Styles.card}>
        <div className={Styles.content}>
          <div className={Styles.modernKYCContainer}>
            <h5>KYC Verification</h5>

            {/* Header */}
            <div className={Styles.kycHeader}>
              <div className={Styles.headerContent}>
                {/* <h5 className={Styles.kycTitle}>KYC Verification</h5> */}
                <p className={Styles.kycSubtitle}>Verify your identity to unlock all platform features</p>
              </div>
            </div>

            {/* User Info Section */}
            <div className={Styles.userInfoSection}>
              <div className={Styles.infoGroup}>
                <span className={Styles.infoLabel}>Official Full Name</span>
                <span className={Styles.infoValue}>
                  {kyc_status === 'verified'
                    ? `${userData.first_name} ${userData.last_name}`
                    : "admin user"}
                </span>
              </div>

              <div className={Styles.infoGroup}>
                <span className={Styles.infoLabel}>UID</span>
                <span className={Styles.infoValue}>UID: {userData.uid}</span>
              </div>
            </div>

            {/* Verification Status Card */}
            <div className={Styles.statusCard}>
              <div className={Styles.statusBackground} />

              <div className={Styles.statusContent}>
                {/* Status Icon */}
                <div
                  className={Styles.statusIcon}
                  style={{ backgroundColor: statusDisplay.color }}
                >
                  <FiCheck size={40} strokeWidth={3} />
                </div>

                <h2
                  className={Styles.statusTitle}
                  style={{ color: statusDisplay.color }}
                >
                  {statusDisplay.title}
                </h2>

                {/* Verification Steps */}
                {statusDisplay.showSteps && (
                  <div className={Styles.verificationSteps}>
                    {verificationSteps.map((step, index) => (
                      <div key={step.id} className={Styles.stepContainer}>
                        {index > 0 && <div className={Styles.stepConnector} />}

                        <div className={Styles.stepItem}>
                          <div className={Styles.stepIcon}>
                            <FiCheck size={10} strokeWidth={2} />
                          </div>
                          <span className={Styles.stepLabel}>{step.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Feature Comparison Table */}
            <div className={Styles.comparisonTable}>
              {/* Table H eader */}
              <div className={Styles.tableHeader}>
                <div className={Styles.headerCell}>
                  <span className={Styles.headerText}>Features</span>
                </div>
                <div className={`${Styles.headerCell} ${Styles.centerCell}`}>
                  <span className={Styles.noKycHeader}>NO KYC</span>
                </div>
                <div className={`${Styles.headerCell} ${Styles.centerCell}`}>
                  <span className={Styles.kycVerifiedHeader}>KYC VERIFIED</span>
                </div>
              </div>

              {/* Table Body */}
              <div className={Styles.tableBody}>
                {features.map((feature, index) => (
                  <div key={index} className={Styles.tableRow}>
                    {/* Feature Info */}
                    <div className={Styles.featureCell}>
                      <h3 className={Styles.featureTitle}>{feature.title}</h3>
                      <p className={Styles.featureDescription}>{feature.description}</p>
                    </div>

                    {/* No KYC Column */}
                    <div className={`${Styles.statusCell} ${Styles.centerCell}`}>
                      <div
                        className={Styles.statusIndicator}
                        style={{ backgroundColor: feature.noKyc ? 'var(--success-color)' : 'var(--danger-color)' }}
                      >
                        {feature.noKyc ? (
                          <FiCheck size={18} strokeWidth={2} />
                        ) : (
                          <FiX size={18} strokeWidth={2} />
                        )}
                      </div>
                    </div>

                    {/* KYC Verified Column */}
                    <div className={`${Styles.statusCell} ${Styles.centerCell}`}>
                      <div
                        className={Styles.statusIndicator}
                        style={{ backgroundColor: feature.kycVerified ? 'var(--success-color)' : 'var(--danger-color)' }}
                      >
                        {feature.kycVerified ? (
                          <FiCheck size={18} strokeWidth={2} />
                        ) : (
                          <FiX size={18} strokeWidth={2} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Account Creation Date */}
            <div className={Styles.tabFooter}>
              This account was created on {formatDateTime(userData?.created_at)}
            </div>

            {/* Hidden KYC Status Component for backward compatibility */}
            <div>
              <KYCStatus status={kyc_status} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KYCVerificationTab